import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import escalationService from '../../../services/escalationService';

const EscalationMonitor = () => {
  const [escalations, setEscalations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEscalation, setSelectedEscalation] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadEscalations();
    loadStats();

    // Subscribe to real-time updates
    const subscription = escalationService?.subscribeToEscalations(
      null, // Will use current user from service
      (payload) => {
        console.log('Escalation update:', payload);
        loadEscalations();
        loadStats();
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const loadEscalations = async () => {
    try {
      setLoading(true);
      const data = await escalationService?.getMyEscalations();
      setEscalations(data || []);
    } catch (error) {
      console.error('Error loading escalations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await escalationService?.getEscalationStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleEscalateToNext = async (escalationId) => {
    try {
      setActionLoading(true);
      const result = await escalationService?.escalateToNextLevel(escalationId);
      if (result?.success) {
        await loadEscalations();
        await loadStats();
      }
    } catch (error) {
      console.error('Error escalating:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolve = async (escalationId) => {
    try {
      setActionLoading(true);
      await escalationService?.resolveEscalation(escalationId, 'Resolved by approver');
      await loadEscalations();
      await loadStats();
    } catch (error) {
      console.error('Error resolving:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async (escalationId, status) => {
    try {
      setActionLoading(true);
      await escalationService?.updateEscalationStatus(escalationId, status);
      await loadEscalations();
      await loadStats();
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      in_progress: 'secondary',
      escalated: 'error',
      resolved: 'success',
      cancelled: 'muted'
    };
    return colors?.[status] || 'muted';
  };

  const getTriggerIcon = (triggerType) => {
    const icons = {
      overdue_approval: 'Clock',
      emergency_travel: 'AlertTriangle',
      compliance_violation: 'ShieldAlert',
      budget_exceeded: 'DollarSign',
      missing_documentation: 'FileX'
    };
    return icons?.[triggerType] || 'AlertCircle';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="glass-container rounded-xl border border-border/50 p-6">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading escalations...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-container rounded-xl border border-border/50">
      {/* Header */}
      <div className="p-6 border-b border-border/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-error/10 border border-error/20 flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-error" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Escalation Monitor</h3>
              <p className="text-sm text-muted-foreground">Active escalations requiring attention</p>
            </div>
          </div>
          {stats && (
            <div className="flex items-center space-x-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-error">{stats?.overdue || 0}</div>
                <div className="text-xs text-muted-foreground">Overdue</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-warning">{stats?.pending || 0}</div>
                <div className="text-xs text-muted-foreground">Pending</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-secondary">{stats?.inProgress || 0}</div>
                <div className="text-xs text-muted-foreground">In Progress</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Escalations List */}
      <div className="p-6">
        {escalations?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="CheckCircle" size={48} className="mx-auto text-success mb-3" />
            <p className="text-muted-foreground">No active escalations</p>
          </div>
        ) : (
          <div className="space-y-4">
            {escalations?.map((escalation) => (
              <div
                key={escalation?.id}
                className="border border-border/50 rounded-lg p-4 hover:bg-muted/20 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className={`w-10 h-10 rounded-lg bg-${getStatusColor(escalation?.status)}/10 border border-${getStatusColor(escalation?.status)}/20 flex items-center justify-center flex-shrink-0`}>
                      <Icon name={getTriggerIcon(escalation?.triggerType)} size={20} className={`text-${getStatusColor(escalation?.status)}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-semibold text-foreground">
                          {escalation?.travelRequest?.requestNumber || 'N/A'}
                        </h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${getStatusColor(escalation?.status)}/10 text-${getStatusColor(escalation?.status)}`}>
                          {escalation?.status?.replace('_', ' ')?.toUpperCase()}
                        </span>
                        {escalation?.autoEscalated && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-secondary/10 text-secondary">
                            AUTO
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {escalation?.escalationReason}
                      </p>
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <span className="text-muted-foreground">Traveler: </span>
                          <span className="text-foreground font-medium">
                            {escalation?.travelRequest?.traveler?.fullName || 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Destination: </span>
                          <span className="text-foreground font-medium">
                            {escalation?.travelRequest?.destination || 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Level: </span>
                          <span className="text-foreground font-medium">
                            {escalation?.currentApproverLevel?.toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Escalated: </span>
                          <span className="text-foreground font-medium">
                            {formatDate(escalation?.escalatedAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {escalation?.status === 'pending' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="ArrowUp"
                          onClick={() => handleEscalateToNext(escalation?.id)}
                          disabled={actionLoading}
                        >
                          Escalate
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Play"
                          onClick={() => handleUpdateStatus(escalation?.id, 'in_progress')}
                          disabled={actionLoading}
                        >
                          Start
                        </Button>
                      </>
                    )}
                    {escalation?.status === 'in_progress' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="ArrowUp"
                          onClick={() => handleEscalateToNext(escalation?.id)}
                          disabled={actionLoading}
                        >
                          Escalate
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          iconName="CheckCircle"
                          onClick={() => handleResolve(escalation?.id)}
                          disabled={actionLoading}
                        >
                          Resolve
                        </Button>
                      </>
                    )}
                    {escalation?.status === 'escalated' && (
                      <Button
                        variant="default"
                        size="sm"
                        iconName="CheckCircle"
                        onClick={() => handleResolve(escalation?.id)}
                        disabled={actionLoading}
                      >
                        Resolve
                      </Button>
                    )}
                  </div>
                </div>

                {escalation?.escalationRule && (
                  <div className="mt-3 pt-3 border-t border-border/30">
                    <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                      <Icon name="Info" size={14} />
                      <span>
                        Rule: {escalation?.escalationRule?.ruleName} - {escalation?.escalationRule?.description}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EscalationMonitor;